'use client';

/**
 * VendorMonogram — a thin painter for the BFF-derived brand mark.
 *
 * All derivation (class tag, initials, colours, asset url) lives server-side:
 * the monogram in `vendor_mark.py`, the real logo in `vendor_logos.py`, both
 * shipped on `VendorSummary` (`monogram*`, `logo`). This component derives
 * NOTHING — it paints the class treatment it was handed, else the coloured
 * monogram floor.
 *
 * Logo classes (containment kills the ragged grid; theme held per class):
 *   mono      — 1-colour wordmark, recoloured to theme on a transparent plate
 *               (multiply on light; invert+screen on dark)
 *   plate     — colored / self-contained mark on a light sign-board
 *   platedark — inverse (white-on-black) mark on a dark sign-board
 *
 * @layer presentation
 * @arch_component_type primitive
 * @arch_domain procurement
 */

import { cn } from '@/lib/utils/cn';

export interface VendorLogo {
  logo_class: 'mono' | 'plate' | 'platedark';
  src: string;
  badge?: string | null;
}

export interface VendorMonogramProps {
  /** BFF-derived initials, e.g. "CF". */
  initials: string;
  /** BFF-derived chip colours (soft tint bg + darker same-hue fg). */
  bg?: string;
  fg?: string;
  /** BFF-shipped real logo — replaces the monogram when present. */
  logo?: VendorLogo | null;
  /** Vendor name, for alt / aria-label. */
  name: string;
  /** Caller sizes the mark via className (e.g. `h-11 w-11 text-sm`). */
  className?: string;
}

export function VendorMonogram({ initials, bg, fg, logo, name, className }: VendorMonogramProps) {
  if (logo) {
    // Plate (container chrome) flips with the class; theme flips with `dark:`.
    const plate =
      logo.logo_class === 'plate'
        ? 'bg-white border border-black/10'
        : logo.logo_class === 'platedark'
          ? 'bg-[#111114] border border-white/10'
          : 'bg-transparent'; // mono
    // mono recolours to the foreground; plate/platedark keep the designed look.
    const imgTreatment =
      logo.logo_class === 'mono'
        ? 'mix-blend-multiply dark:mix-blend-screen dark:invert'
        : '';
    return (
      <div
        aria-label={name}
        className={cn('flex items-center justify-center overflow-hidden rounded-lg p-2', plate, className)}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={logo.src}
          alt={name}
          className={cn('max-h-full max-w-full object-contain', imgTreatment)}
        />
      </div>
    );
  }
  return (
    <div
      aria-label={name}
      className={cn(
        'flex items-center justify-center rounded-lg font-sans font-bold select-none',
        className,
      )}
      style={{ background: bg, color: fg }}
    >
      {initials}
    </div>
  );
}
